// The factory's vocabulary: structure kinds, material lines, the unit word,
// and nothing else.
//
// Shared by both trees and owned by neither — see `board.ts`. Every number
// here is in a hash or a save, so it is appended to and never renumbered.
// The engine's `industry/state.ts` and `materials.ts` re-export what is theirs.

/** The kinds of building. Numbers, like every other tag in the rules layer. */
export const STORE = 0;
/** The Laser Turret (§31 S8) — the id the generic turret had. */
export const TURRET = 1;
// **The artifact structures** (§26 F6): one kind per power. Appended, never
// renumbered: the kind is in the industry hash of every board that has ever
// had a warehouse on it.
export const ALTAR = 2;
export const RELIC = 3;
/** 4 was the Banner, retired with standing orders (§31 R1). Never reused. */
export const KEYSTONE = 5;
export const WELL = 6;
/** 7 was the Research Station, retired (§31 R1). Never reused. */
/** **Walls** (§31 S4): Simple, Firm, Reinforced. */
export const SIMPLE_WALL = 8;
export const FIRM_WALL = 9;
export const REINFORCED_WALL = 10;
/** **The Armory** (§31 S5): an upgrade site. */
export const ARMORY = 11;
/** **The power network** (§31 S6). */
export const COAL_GENERATOR = 12;
export const NUCLEAR_GENERATOR = 13;
export const BATTERY = 14;
export const CABLING_TOWER = 15;
/**
 * **Trains** (§31 S7): Train Track, Train Engine, Train Car. Walled Track
 * (Spike Drivers, X2) will take the next free number.
 */
export const TRACK = 16;
export const TRAIN_ENGINE = 17;
export const TRAIN_CAR = 18;
/**
 * **Artifacts** (§31 A6–A9), appended: the Fusion Chamber (A8), the Shield
 * Generator (Deflector Shields, A9), the Pillar of Excellence (Artisan
 * Pillar, A6). Kind 5 (`KEYSTONE`) is the Targeting Array's Mainframe (A7).
 */
export const FUSION_CHAMBER = 19;
export const SHIELD_GENERATOR = 20;
export const ARTISAN_PILLAR = 21;
/**
 * **Turrets** (§31 S8): kind 1 (`TURRET`) is the Laser Turret; the Kinetic
 * and Cannon Turrets appended.
 */
export const KINETIC_TURRET = 22;
export const CANNON_TURRET = 23;
/**
 * **Artifacts** (§31 A10–A13), appended: the Propaganda Tower (Stoke the
 * Forges), the House of Cards (My Deck Runith Over), the Endurance Monument
 * (Yield Not), the Fountain of Fiendishness (Flow Faster).
 */
export const PROPAGANDA_TOWER = 24;
export const HOUSE_OF_CARDS = 25;
export const ENDURANCE_MONUMENT = 26;
export const FIEND_FOUNTAIN = 27;
/** **Legion Mandate** (§33 T5), appended: the Legion Tactics Tower. */
export const LEGION_TOWER = 28;
/** **The Drone-Assisted Warehouse** (§33 I5), appended: the store's upgrade. */
export const DRONE_WAREHOUSE = 29;
/** **The Empower Tower** (§33 S1), appended: pairs make extra moves near a fuel source. */
export const EMPOWER_TOWER = 30;
export type StructureKind =
  | typeof STORE
  | typeof TURRET
  | typeof ALTAR
  | typeof RELIC
  | typeof KEYSTONE
  | typeof WELL
  | typeof SIMPLE_WALL
  | typeof FIRM_WALL
  | typeof REINFORCED_WALL
  | typeof ARMORY
  | typeof COAL_GENERATOR
  | typeof NUCLEAR_GENERATOR
  | typeof BATTERY
  | typeof CABLING_TOWER
  | typeof TRACK
  | typeof TRAIN_ENGINE
  | typeof TRAIN_CAR
  | typeof FUSION_CHAMBER
  | typeof SHIELD_GENERATOR
  | typeof ARTISAN_PILLAR
  | typeof KINETIC_TURRET
  | typeof CANNON_TURRET
  | typeof PROPAGANDA_TOWER
  | typeof HOUSE_OF_CARDS
  | typeof ENDURANCE_MONUMENT
  | typeof FIEND_FOUNTAIN
  | typeof LEGION_TOWER
  | typeof DRONE_WAREHOUSE
  | typeof EMPOWER_TOWER;

/**
 * The four material lines (§26 F2, §31 M1). Metal and stone are **ladders**
 * (refined a rung at a time, a higher rung pays for a lower); special and
 * fuel are **resources** (never refined, each grade its own thing). Fuel is
 * appended: the line packs in two bits and 3 was free.
 */
export const METAL = 0;
export const STONE = 1;
export const SPECIAL = 2;
export const FUEL = 3;
export type MaterialLine = typeof METAL | typeof STONE | typeof SPECIAL | typeof FUEL;

/** The eight materials, in ladder order: the stable id a client keys on. */
export const MATERIAL_IDS = [
  "iron",
  "steel",
  "crucible-steel",
  "cobble",
  "stone",
  "marble",
  "electrum",
  "diamond"
] as const;
export type MaterialId = (typeof MATERIAL_IDS)[number];

/**
 * The fuel line's ids (§31 M1) — **their own list, not appended to
 * `MATERIAL_IDS`**, which a client keys a display table on: fuel is burned,
 * never priced, so no bill row names it. `ResourceId` is either.
 */
export const FUEL_IDS = ["coal", "nuclear"] as const;
export type FuelId = (typeof FUEL_IDS)[number];
export type ResourceId = MaterialId | FuelId;

// --- the unit word ----------------------------------------------------------
//
// A unit packs into one Uint32 — **grade in the low nibble, the line in the
// two bits above it, the refinery bitmask above that**.

/**
 * The grade nibble's bound — five rungs, from arm B's metal line, retired
 * (§31 R3). **Kept at 4 though no line climbs past 2**: a `units-<slot>` id is
 * `line * GRADE_SLOTS + grade` on both sides of the line, so shrinking it
 * renumbers every slot. How far refining goes is `materials.ts`'s `lineTop`.
 */
export const MAX_GRADE = 4;
export const GRADE_SLOTS = MAX_GRADE + 1;

export const makeUnit = (grade: number, touched = 0, line: MaterialLine = METAL): number =>
  ((touched << 6) | ((line & 0x3) << 4) | (grade & 0xf)) >>> 0;

export const unitGrade = (unit: number): number => unit & 0xf;

/** Which line this unit is on. Meaningful for material only — see above. */
export const unitLine = (unit: number): MaterialLine => ((unit >>> 4) & 0x3) as MaterialLine;

export const unitTouched = (unit: number): number => unit >>> 6;

/**
 * Grade nibble 15 was the artifact marker, and the bits above it a card
 * ordinal — retired with artifact units (§31 R1). Never reused: a nibble of 15
 * is not a grade any line has.
 */
